/**
 * Account switch („Als diese Person anmelden“ / „Zurück zu meinem Account“ / test login): where the app opens after the
 * reload and a silent first navigation (no „Diese Seite ist für dich nicht freigegeben.“ because the page of the previous
 * account is not open for the new one). Pure decisions (switchTarget, routeTitle) are checked in
 * tests/teacherPortal/accountSwitch.check.ts; the storage functions never throw (private mode / blocked storage).
 */
import {isApplicantPortalPath, isNoAccessPath, isTeacherPortalPath} from '@/helpers/portalMode';

/** sessionStorage: set right before the reload of an account switch, read by the first route check after it */
export const ACCOUNT_SWITCH_KEY = 'vanilla-account-switch';
/** localStorage: office page before „Als diese Person anmelden“ (target of „Zurück zu meinem Account“) */
export const LOGIN_AS_FROM_KEY = 'login-as-from';
/** localStorage: page before the test login (roles overview, existing key) */
export const TEMP_LOGIN_FROM_KEY = 'temp-login-from';

export type AccountSwitchState = { notice?: string };

const isPortalPath = (path: string) => isTeacherPortalPath(path) || isApplicantPortalPath(path) || isNoAccessPath(path);

/**
 * Page to open after the switch (null = stay on the current page; the route check then leads portal logins silently to their area).
 * - loginAs: stay – a teacher login lands silently on /my-teaching/today, an applicant login on /my-application
 * - origin: the office page before the switch (test login or „Als diese Person anmelden“), else „/“ if the current page is
 *   in the Lehrkraft-/Bewerbungs-Bereich (the office account usually may not open it)
 * - temp: always the start page
 */
export function switchTarget(kind: 'loginAs' | 'origin' | 'temp', currentPath: string, stored: { tempFrom?: string | null; loginAsFrom?: string | null } = {}): string | null {
    if (kind === 'temp') {
        return '/';
    }
    if (kind === 'loginAs') {
        return null;
    }
    const remembered = [stored.tempFrom, stored.loginAsFrom].find((path) => !!path && path.startsWith('/') && !isPortalPath(path.split('?')[0]));
    if (remembered) {
        return remembered as string;
    }
    return isPortalPath(currentPath) ? '/' : null;
}

/**
 * Title of the page after a navigation: always the name of the target route (also after a silent redirect, e.g.
 * /my-application → /my-teaching/today). Profile full views (?tab=) keep the title of the page (null = keep).
 */
export function routeTitle(to: { name?: string | null; path: string; meta?: { keepOnQueryChange?: boolean } }, from: { path: string } | null): string | null {
    if (to.meta?.keepOnQueryChange && from && to.path === from.path) {
        return null;
    }
    return to.name || '';
}

function storage(kind: 'local' | 'session'): Storage | null {
    try {
        return kind === 'local' ? window.localStorage : window.sessionStorage;
    } catch (e) {
        return null;
    }
}

function read(kind: 'local' | 'session', key: string): string | null {
    try {
        return storage(kind)?.getItem(key) ?? null;
    } catch (e) {
        return null;
    }
}

function write(kind: 'local' | 'session', key: string, value: string | null) {
    try {
        if (value === null) {
            storage(kind)?.removeItem(key);
        } else {
            storage(kind)?.setItem(key, value);
        }
    } catch (e) {
        // blocked storage: the switch still works, only without the remembered page / silent first navigation
    }
}

/** Remembers the office page before „Als diese Person anmelden“ */
export function rememberLoginAsFrom(path: string) {
    if (path && !isPortalPath(path.split('?')[0])) {
        write('local', LOGIN_AS_FROM_KEY, path);
    }
}

/** Drops the remembered office page (way back not possible anymore) */
export function forgetLoginAsFrom() {
    write('local', LOGIN_AS_FROM_KEY, null);
}

/** Marks the coming reload as account switch (optional notice shown after it, e.g. „Löschung für … beantragt“) */
export function markAccountSwitch(state: AccountSwitchState = {}) {
    write('session', ACCOUNT_SWITCH_KEY, JSON.stringify(state));
}

/** Read once by the first route check after the reload (null = no account switch) */
export function consumeAccountSwitch(): AccountSwitchState | null {
    const raw = read('session', ACCOUNT_SWITCH_KEY);
    if (raw === null) {
        return null;
    }
    write('session', ACCOUNT_SWITCH_KEY, null);
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
        return {};
    }
}

/**
 * Reloads the app after an account switch at `path` (hash route; null = current page). The address is replaced without
 * a router navigation: the router would still check the page with the rights of the previous account.
 */
export function reloadAfterSwitch(kind: 'loginAs' | 'origin' | 'temp', currentPath: string, state: AccountSwitchState = {}) {
    const target = switchTarget(kind, currentPath, {tempFrom: read('local', TEMP_LOGIN_FROM_KEY), loginAsFrom: read('local', LOGIN_AS_FROM_KEY)});
    if (kind === 'origin') {
        write('local', TEMP_LOGIN_FROM_KEY, null);
        write('local', LOGIN_AS_FROM_KEY, null);
    }
    markAccountSwitch(state);
    if (target !== null) {
        try {
            history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}#${target}`);
        } catch (e) {
            // the route check leads to the right area anyway
        }
    }
    window.location.reload();
}
